import React, { useRef } from "react";
import { cn } from "../../lib/utils";

/**
 * Abas segmentadas, no conceito do app.flamex (.typing-segmented): trilho neutro e a aba ativa
 * "elevada" sobre ele. Só tokens de tema — nada de literal de cor aqui (vaza para todas as telas).
 *
 * Acessível: role tablist/tab, aria-selected, e navegação por setas, Home e End.
 * Use tabPanelProps(idPrefix, value) no conteúdo de cada aba para ligar aba e painel.
 */
export default function Tabs({ items, value, onChange, idPrefix = "tabs", ariaLabel, className = "" }) {
  const listRef = useRef(null);

  const selectByIndex = (index) => {
    const item = items[index];
    if (!item) return;
    onChange?.(item.value);
    listRef.current?.querySelectorAll('[role="tab"]')?.[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    const last = items.length - 1;
    const keyToIndex = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    if (!(event.key in keyToIndex)) return;
    event.preventDefault();
    selectByIndex(keyToIndex[event.key]);
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex max-w-full gap-1 overflow-x-auto rounded-[9px] border border-border bg-muted p-[3px]",
        className
      )}
    >
      {items.map((item, index) => {
        const active = item.value === value;
        const Icon = item.icon;

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.value}`}
            aria-selected={active}
            aria-controls={`${idPrefix}-panel-${item.value}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange?.(item.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              "inline-flex h-[38px] shrink-0 items-center justify-center gap-[7px] whitespace-nowrap rounded-[7px] px-[13px]",
              "text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function tabPanelProps(idPrefix, value) {
  return {
    role: "tabpanel",
    id: `${idPrefix}-panel-${value}`,
    "aria-labelledby": `${idPrefix}-tab-${value}`,
  };
}
